import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-max-brand focus-visible:ring-offset-2 focus:ring-4 focus:ring-max-brand focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 min-h-[44px] touch-manipulation",
  {
    variants: {
      variant: {
        default: "bg-max-brand text-white hover:bg-max-brand-hover shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]",
        primary: "bg-max-brand text-white hover:bg-max-brand-hover shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]",
        blue: "bg-primary text-primary-foreground hover:bg-primary-hover shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]",
        outline: "border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98]",
        ghost: "hover:bg-max-brand/10 hover:text-max-brand transform hover:scale-[1.02] active:scale-[0.98]",
        link: "text-max-brand underline-offset-4 hover:underline transform hover:scale-[1.02] active:scale-[0.98]",
        save: "bg-neutral-100 text-neutral-600 border border-neutral-300 hover:bg-neutral-200 hover:text-neutral-700 transform hover:scale-[1.02] active:scale-[0.98]",
        unsave: "bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 hover:text-red-700 transform hover:scale-[1.02] active:scale-[0.98]",
        success: "bg-green-600 text-white hover:bg-green-700 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]",
        warning: "bg-amber-500 text-white hover:bg-amber-600 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
